import {
  API_LATENCY_BUCKETS,
  API_LATENCY_OPERATION_MAX_LENGTH,
  API_LATENCY_SURFACES,
  type ApiLatencyRevisions,
} from '@headline/schemas'
import { keepPreviousData, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { LatencyFilters, type LatencyFilterValues } from '@/components/api-latency/LatencyFilters'
import { LatencyTable } from '@/components/api-latency/LatencyTable'
import { NotConnected, NotReady, Panel, TrendPanel } from '@/components/api-latency/LatencyPanels'
import { RevisionTable } from '@/components/api-latency/RevisionTable'
import { PageHeader } from '@/components/PageHeader'
import {
  LATENCY_RANGE_VALUES,
  bucketFor,
  chartTarget,
  isLatencySurface,
  latencyRead,
  latencyWindow,
  operationOptions,
} from '@/lib/api-latency-view'
import {
  latencyEndpointsQuery,
  latencyRevisionsQuery,
  latencySummaryQuery,
  latencyTrendQuery,
} from '@/lib/api-latency-queries'

/**
 * How fast the public API (clipbook-api) answers, for Clipbook staff (CLI-1041) — Clipbook Internal, every
 * associate included, never a client: a client's own API page is the workspace's (`…/api`). Behind
 * `/api/api-latency` (`requireClipbookStaff()`); the loader waits on the endpoint list, which is behind the
 * same gate, so a refused visitor sees the gate's own sentence and nothing else. The filters are search
 * params, so a view is a link. clipbook-api not connected (a preview without its URL and token) reads as
 * that, never as zeros.
 */
const searchSchema = z.object({
  range: z.enum(LATENCY_RANGE_VALUES).default('7d').catch('7d'),
  bucket: z.enum(API_LATENCY_BUCKETS).default('hour').catch('hour'),
  surface: z.enum(API_LATENCY_SURFACES).optional().catch(undefined),
  operation: z
    .string()
    .trim()
    .min(1)
    .max(API_LATENCY_OPERATION_MAX_LENGTH)
    .optional()
    .catch(undefined),
  endpoint: z.uuid().optional().catch(undefined),
})

export const Route = createFileRoute('/app/api/latency')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    range: search.range,
    surface: search.surface,
    endpoint: search.endpoint,
  }),
  loader: async ({ context, deps }) => {
    await context.queryClient.ensureQueryData(latencyEndpointsQuery)
    // Started, not awaited: clipbook-api may be slow or absent, and the page says so where it shows.
    void context.queryClient.prefetchQuery(
      latencySummaryQuery({
        ...latencyWindow(deps.range, new Date()),
        surface: deps.surface,
        endpointId: deps.endpoint,
      }),
    )
  },
  component: ApiLatencyPage,
})

function useLatencyReads(search: z.infer<typeof searchSchema>) {
  const span = latencyWindow(search.range, new Date())
  const bucket = bucketFor(search.range, search.bucket)
  const base = { ...span, surface: search.surface, endpointId: search.endpoint }
  // The table lists every operation under the surface and endpoint; the operation picks what is charted.
  const summaryQuery = useQuery({ ...latencySummaryQuery(base), placeholderData: keepPreviousData })
  const summary = latencyRead(summaryQuery)
  const target = summary.state === 'ready' ? chartTarget(summary.data, search) : null
  const trendQuery = useQuery({
    ...latencyTrendQuery(
      { ...base, surface: target?.surface, operation: target?.operation },
      bucket,
    ),
    enabled: target !== null,
  })
  const revisionsQuery = useQuery(latencyRevisionsQuery({ ...base, operation: search.operation }))
  return {
    bucket,
    summary,
    target,
    trend: latencyRead(trendQuery),
    revisions: latencyRead<ApiLatencyRevisions>(revisionsQuery),
  }
}

function ApiLatencyPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: endpoints } = useSuspenseQuery(latencyEndpointsQuery)
  const { bucket, summary, target, trend, revisions } = useLatencyReads(search)
  const setFilters = (next: Partial<LatencyFilterValues>) =>
    void navigate({ search: (previous) => ({ ...previous, ...next }), replace: true })
  const summaryData = summary.state === 'ready' ? summary.data : null
  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="API latency"
        description="How fast clipbook-api answers its clients, REST and MCP. Clipbook staff only."
        className="mb-0"
      />
      <p className="max-w-3xl text-sm text-muted-foreground">
        Response-built latency: from a request entering clipbook-api until its response is built —
        authentication, rate limits, every ClickHouse read and the JSON. Not the network time to the
        client. Errors and timeouts stay in the percentiles (a timeout is latency the client felt);
        5xx and timeouts are counted beside them.
      </p>
      <LatencyFilters
        values={{ ...search, bucket }}
        operations={operationOptions(summaryData, search.surface, search.operation)}
        endpoints={endpoints.endpoints}
        onChange={setFilters}
      />
      {summary.state === 'unconfigured' ? (
        <NotConnected />
      ) : (
        <>
          <TrendPanel summary={summary} trend={trend} target={target} bucket={bucket} />
          <Panel title="By operation" description="This range against the seven days before it.">
            {summaryData ? (
              <LatencyTable
                summary={summaryData}
                charted={target}
                onChart={(op) =>
                  setFilters({
                    surface: isLatencySurface(op.surface) ? op.surface : undefined,
                    operation: op.operation,
                  })
                }
              />
            ) : (
              <NotReady read={summary} loading="Loading latency…" />
            )}
          </Panel>
          <Panel
            title="By revision"
            description="Per Cloud Run revision that served the range, newest first: how a deploy moved latency."
          >
            {revisions.state === 'ready' ? (
              <RevisionTable revisions={revisions.data.revisions} />
            ) : (
              <NotReady read={revisions} loading="Loading revisions…" />
            )}
          </Panel>
        </>
      )}
    </div>
  )
}
